import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/base/Button';
import { LeadTypePill, PriorityPill, VerifyPill } from '@/components/base/StatusPill';
import { useAuth } from '@/context/AuthContext';
import { listPoolLeads, type PoolLeadSummary } from '@/services/leadsApi';

export default function PublicLeadsPreview() {
  const navigate = useNavigate();
  const { user, requestLogin } = useAuth();
  const isLoggedIn = Boolean(user);
  const [leads, setLeads] = useState<PoolLeadSummary[]>([]);

  useEffect(() => {
    let cancelled = false;
    listPoolLeads({ page: 1, pageSize: 6 })
      .then((data) => {
        if (!cancelled) setLeads(data.items.slice(0, 6));
      })
      .catch(() => {
        if (!cancelled) setLeads([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleOpen = (id: string) => {
    const go = () => navigate(`/leads/${encodeURIComponent(id)}`);
    if (!isLoggedIn) {
      requestLogin(go);
      return;
    }
    go();
  };

  return (
    <section className="border-y border-background-200 bg-background-100/60">
      <div className="mx-auto w-full max-w-[1440px] px-4 py-16 md:px-8 md:py-20">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-widest text-accent-700">
              公开线索摘要
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-900 md:text-3xl">
              先看看现在的潜在线索
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              未登录可浏览线索摘要。展开详情、收藏或加入目标客户时，将提示你登录，登录后会回到当前线索位置。
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate('/leads')} icon="ri-arrow-right-line">
            进入潜在线索池
          </Button>
        </div>

        {leads.length === 0 ? (
          <p className="text-sm text-foreground-500">暂无公开线索摘要，请稍后进入潜在线索池查看。</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {leads.map((lead) => (
              <article
                key={lead.id}
                className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-heading text-base font-semibold leading-snug text-foreground-900">
                      {lead.title || lead.name}
                    </h3>
                    <p className="mt-1 break-words text-xs leading-relaxed text-foreground-500">
                      {[lead.companyName, lead.creditCode, lead.region, lead.industry]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  <VerifyPill value={lead.verifyStatus} />
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <LeadTypePill value={lead.leadType} />
                  <PriorityPill value={lead.priority} />
                </div>

                <p className="mt-4 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-600">
                  {isLoggedIn ? lead.triggerEvent : `${lead.triggerEvent.slice(0, 42)}…`}
                </p>

                <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-4">
                  <span className="text-[11px] text-foreground-400">
                    {isLoggedIn ? (
                      <>发布 {lead.publishDate}</>
                    ) : (
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-lock-line" />
                        登录查看发布时间与来源
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleOpen(lead.id)}
                    className="inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-800"
                  >
                    {isLoggedIn ? '查看详情' : '登录后查看'}
                    <i className="ri-arrow-right-line" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
